// client/src/pages/Dashboard.jsx
import React, { useEffect, useState, useContext } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { ProjectContext } from '../contexts/ProjectContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const Dashboard = () => {
  const { signOut } = useAuth();
  const { activeProject, setActiveProject } = useContext(ProjectContext);
  const [projects,  setProjects]  = useState([]);
  const [newName,   setNewName]   = useState('');
  const [totalCost, setTotalCost] = useState(0);
  const [designProjects, setDesignProjects] = useState([]);
  const [designLoading, setDesignLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const { data: projectList } = await api.get('/projects');
        setProjects(projectList);

        const perProjectTotals = await Promise.all(
          projectList.map(async (p) => {
            try {
              const { data: tables } = await api.get(`/projects/${p._id}/tables`);
              return tables.reduce((sum, t) => sum + (t.totalCost || 0), 0);
            } catch (err) {
              console.error(`Failed to load tables for project ${p._id}`, err);
              return 0;
            }
          })
        );
        setTotalCost(perProjectTotals.reduce((a, b) => a + b, 0));
      } catch (err) {
        console.error('Failed to load projects', err);
        toast.error('🚨 Could not load project data.');
      }
    };
    load();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadDesign = async () => {
      setDesignLoading(true);
      try {
        const { data } = await api.get('/design/conversations');
        if (cancelled) return;
        const items = (data?.conversations || [])
          .filter((conv) => conv?.renderUrl || (Array.isArray(conv?.renders) && conv.renders.some((r) => r?.url)))
          .slice(0, 3);
        const normalized = items.map((conv) => ({
          id: conv.id,
          title: conv.title || 'Design project',
          summary: conv.imageSummary || conv.description || 'No summary yet.',
          updatedAt: conv.updatedAt || conv.createdAt,
          image:
            conv.renderUrl ||
            (Array.isArray(conv.renders) ? conv.renders.find((r) => r.url)?.url : '') ||
            '',
        }));
        setDesignProjects(normalized);
      } catch (err) {
        console.error('Failed to load design conversations', err);
      } finally {
        if (!cancelled) setDesignLoading(false);
      }
    };
    loadDesign();
    return () => { cancelled = true; };
  }, []);

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.warn('⚠️ Please enter a project name.');
      return;
    }
    try {
      const { data: newProject } = await api.post('/projects', { name: newName });
      setProjects((prev) => [newProject, ...prev]);
      setNewName('');
      toast.success('🎉 Project created.');
      setActiveProject(newProject);
      navigate(`/projects/${newProject._id}/estimate`);

      try {
        const { data: tables } = await api.get(`/projects/${newProject._id}/tables`);
        const newProjTotal = tables.reduce((sum, t) => sum + (t.totalCost || 0), 0);
        setTotalCost((prev) => prev + newProjTotal);
      } catch (err) {
        console.error('Error loading tables for new project', err);
      }
    } catch (error) {
      console.error('Error creating project', error);
      toast.error('🚨 Error creating project.');
    }
  };

  // Tokens
  const card = 'glass-card';
  const input = 'glass-input w-full';
  const btnPrimary = 'glass-cta text-sm';
  const btnGhost = 'glass-ghost text-sm';

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 text-zinc-200">
      {/* Header row */}
      <div className="mb-8 flex items-center justify-between">
        <h2 className="text-3xl font-semibold tracking-tight">
          <span className="bg-gradient-to-r from-amber-200 to-amber-500 bg-clip-text text-transparent">
            Dashboard
          </span>
        </h2>
        <button onClick={signOut} className={btnGhost}>Logout</button>
      </div>

      {/* Quick start row */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8 mx-auto max-w-5xl">
        {/* Start Design Assistant */}
        <div className={`${card} relative overflow-hidden p-6 flex flex-col justify-between`}>
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-amber-500/10 blur-2xl" />
            <div className="absolute right-6 top-6 h-10 w-10 rotate-12 rounded-lg border border-amber-400/50 bg-amber-400/15" />
          </div>
          <div className="relative">
            <div className="text-sm uppercase tracking-wide text-amber-300">Get Started</div>
            <h3 className="mt-1 text-xl font-semibold text-white">Design Assistant</h3>
            <p className="mt-1 text-sm text-zinc-400">
              Chat about your project or upload a photo to draft a materials list, then send it to the estimator.
            </p>
          </div>
          <div className="mt-4 relative">
            <button className={btnPrimary} onClick={() => navigate('/')}>
              Start Design
            </button>
          </div>
        </div>

        {/* Estimator */}
        <div className={`${card} relative overflow-hidden p-6 flex flex-col justify-between`}>
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -left-10 top-0 h-24 w-24 rounded-full bg-emerald-400/10 blur-2xl" />
            <div className="absolute left-6 top-6 h-10 w-10 rotate-3 rounded-lg border border-emerald-300/50 bg-emerald-300/15" />
          </div>
          <div className="relative">
            <div className="text-sm uppercase tracking-wide text-emerald-300">Pricing</div>
            <h3 className="mt-1 text-xl font-semibold text-white">Estimator</h3>
            <p className="mt-1 text-sm text-zinc-400">
              Upload a parts list or type items to price them live. Resume your last run anytime.
            </p>
          </div>
          <div className="mt-4 relative">
            <button className={btnPrimary} onClick={() => navigate('/estimate')}>
              Go to Estimator
            </button>
          </div>
        </div>

        {/* Vendor DB */}
        <div className={`${card} relative overflow-hidden p-6 flex flex-col justify-between`}>
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -right-8 bottom-0 h-24 w-24 rounded-full bg-lime-400/10 blur-2xl" />
            <div className="absolute right-6 bottom-6 h-10 w-10 -rotate-6 rounded-lg border border-lime-300/50 bg-lime-300/15" />
          </div>
          <div className="relative">
            <div className="text-sm uppercase tracking-wide text-lime-300">Vendors</div>
            <h3 className="mt-1 text-xl font-semibold text-white">Vendor Database</h3>
            <p className="mt-1 text-sm text-zinc-400">
              Manage your private vendor price lists. Upload CSVs or add items manually, then search from the estimator.
            </p>
          </div>
          <div className="mt-4 relative">
            <button className={btnPrimary} onClick={() => navigate('/vendors')}>
              Manage Vendors
            </button>
          </div>
        </div>
      </div>

      {/* Create Project */}
      <div className={`${card} p-6 mb-8`}>
        <label htmlFor="projectName" className="mb-2 block text-sm text-zinc-400">
          New project
        </label>
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            id="projectName"
            className={input}
            placeholder="New project name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <button onClick={handleCreate} className={btnPrimary}>
            Create
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className={`${card} p-6 text-center`}>
          <div className="text-sm uppercase tracking-wide text-zinc-400">Active</div>
          <div className="mt-1 text-2xl font-bold">
            {projects.length} {projects.length === 1 ? 'Project' : 'Projects'}
          </div>
        </div>

        <div className={`${card} p-6 text-center`}>
          <div className="text-sm uppercase tracking-wide text-zinc-400">Total Cost</div>
          <div className="mt-1 text-2xl font-bold">
            {totalCost === null
              ? '—'
              : totalCost.toLocaleString(undefined, {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
          </div>
        </div>
      </div>

      {/* Active Design Projects */}
      <div className="mt-8">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Active Design Projects</h3>
          <button
            onClick={() => navigate('/')}
            className="text-sm font-semibold text-amber-300 hover:text-amber-200"
          >
            Open Design Assistant
          </button>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {designLoading && (
            <div className={`${card} p-4 text-sm text-zinc-400`}>Loading designs…</div>
          )}
          {!designLoading && !designProjects.length && (
            <div className={`${card} p-4 text-sm text-zinc-400`}>
              No design projects yet. Create one in the Design Assistant.
            </div>
          )}
          {designProjects.map((proj) => (
            <div key={proj.id} className={`${card} overflow-hidden`}>
              <div className="h-40 w-full overflow-hidden rounded-t-2xl bg-black">
                <img
                  src={
                    proj.image ||
                    'https://app.arch-intelligence.ai/logo192.png'
                  }
                  alt={proj.title}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-4 space-y-2">
                <div className="text-sm uppercase tracking-wide text-emerald-300">Design</div>
                <div className="text-base font-semibold text-white truncate">{proj.title}</div>
                <p className="text-sm text-zinc-400 line-clamp-2">{proj.summary}</p>
                {proj.updatedAt && (
                  <div className="text-xs text-zinc-500">
                    Updated {new Date(proj.updatedAt).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
